import React, {useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import ReactSelect from '../../../Common/SelectDropdown/SelectDropdown';
import Ellipses from '../../../Common/EllipsesControl/EllipsesPresets';
import {getAllStEntities} from '../../../../../src/selectors/Ares/selectors';
import {labels} from '../../../../../src/util/utils';
import {useDispatch, useSelector} from 'react-redux';
import {useEngagementId} from '../../../../../src/util/customHooks';
import {GetHelixProjectFromCanvas} from '../../../../../src/actions/HelixIntegration/engagementhelixaction';
import {trialBalanceCategories} from '../../../../../src/util/uiconstants';

export default function StEntityDropdown({stEntityId, onEntitySelected, isIndividualAccount = false, entities}) {
	const allStEntities = useSelector((state) => getAllStEntities(state));
	const stEntities = !isIndividualAccount ? allStEntities : entities;

	const [currentStEntityId, setCurrentStEntityId] = useState(stEntityId ?? 0);
	const engagementId = useEngagementId();

	const dispatch = useDispatch();

	const getHelixProjectFromCanvas = useCallback((selectedStEntityId) => {
		if (selectedStEntityId > 0) {
			dispatch(
				GetHelixProjectFromCanvas(engagementId, {
					trialBalanceTypeCategory: trialBalanceCategories.Planning,
					IncludeConnections: true,
					multiEntityIds: selectedStEntityId
				})
			);
		}
	});

	useEffect(() => {
		if (currentStEntityId !== stEntityId) {
			setCurrentStEntityId(stEntityId ?? 0);
			getHelixProjectFromCanvas(stEntityId);
		}
	}, [stEntityId]);

	const entityOptions = useMemo(() => {
		// Function to sort alphanumeric order
		const alphanumericSort = (a, b) => {
			const nameA = a.label.toLowerCase();
			const nameB = b.label.toLowerCase();
			return nameA.localeCompare(nameB, undefined, {numeric: true, sensitivity: 'base'});
		};

		// Extract the first item (allEntities)
		const allEntitiesOption = {
			value: 0,
			label: labels.allEntities,
			isSelected: stEntityId === 0
		};

		// Sort the rest of the options
		const sortedEntities = stEntities
			.map((entity) => ({
				value: entity?.id,
				label: `${entity?.stEntityShortName} ${entity?.stEntityName}`,
				isSelected: entity?.id === stEntityId,
				...entity
			}))
			.sort(alphanumericSort);

		// Combine the first item with the sorted options
		return [allEntitiesOption, ...sortedEntities];
	}, [stEntities, stEntityId]);

	const optionCount = entityOptions.length;
	const isDisabled = optionCount === 0;

	const formatOptionLabel = (option) => (
		<section className="selectText">
			<Ellipses.Single>{option.label}</Ellipses.Single>
		</section>
	);

	const handleOnChange = (option) => {
		setCurrentStEntityId(option.value);
		onEntitySelected && onEntitySelected(option.value);
		getHelixProjectFromCanvas(option.value);
	};

	const selectedOption = useMemo(() => entityOptions.find((entity) => entity.value === currentStEntityId), [
		currentStEntityId,
		entityOptions
	]);

	return (
		<StyledStEntitiesDropdown className="StyledStEntitiesDropdown">
			<ReactSelect
				formatOptionLabel={formatOptionLabel}
				id="stEntities-dropdown"
				isSearchable
				formLabel={optionCount > 0 ? labels.meSelectEntity : undefined}
				placeholder={optionCount === 0 ? labels.meNoEntitiesAvailable : undefined}
				defaultValue={selectedOption}
				noOptionsMessage={() => labels.noResultsFound}
				options={entityOptions}
				onChange={handleOnChange}
				value={selectedOption}
				isDisabled={isDisabled}
			/>
		</StyledStEntitiesDropdown>
	);
}

const StyledStEntitiesDropdown = styled.section`
	.SelectDropdownContainer {
		.react-select-container {
			.react-select__menu {
				.react-select__menu-list {
					.react-select__option {
						.selectText {
							font-size: var(--px-14);
							font-weight: var(--px-300);
							line-height: var(--px-24);
							color: var(--neutrals-900);
						}
					}
				}
			}
		}
	}
`;
